import { Trash2 } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

import type { Alert } from "../../watchlist/types";
import ModalDragStrip from "../ModalDragStrip";

type AlertDeleteDialogProps = {
  alert: Alert | null;
  busy?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
};

/** Confirmation shared by the rail menu and the alert detail toolbar. */
const AlertDeleteDialog = ({
  alert,
  busy = false,
  error = null,
  onCancel,
  onConfirm,
}: AlertDeleteDialogProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: "watchlist" });
  const { t: tRoot } = useTranslation();

  if (!alert) return null;

  const name = alert.title || alert.prompt;

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/55 px-4 py-8"
      aria-labelledby="alert-delete-title"
      aria-describedby="alert-delete-message"
      role="alertdialog"
      aria-modal="true"
    >
      <button
        type="button"
        aria-label={tRoot("cancel")}
        className="absolute inset-0 cursor-default"
        disabled={busy}
        onClick={onCancel}
      />
      <ModalDragStrip />
      <div className="relative z-10 w-full max-w-md rounded-xl border border-outline bg-surface p-5 text-text shadow-2xl">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-danger/10 text-danger">
            <Trash2 size={20} />
          </div>
          <div className="min-w-0">
            <h2 id="alert-delete-title" className="text-lg font-semibold text-text">
              {t("delete_alert_title")}
            </h2>
            <p id="alert-delete-message" className="mt-2 text-sm leading-6 text-text-mid">
              {t("delete_alert_message", { title: name })}
            </p>
          </div>
        </div>

        {error && <p className="mt-4 text-sm text-danger">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="rounded-lg border border-outline bg-surface px-3 py-2 text-sm font-medium text-text transition hover:bg-muted-surface disabled:opacity-60"
          >
            {tRoot("cancel")}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={() => onConfirm()}
            className="rounded-lg border border-danger bg-danger px-3 py-2 text-sm font-medium text-white transition hover:bg-danger-strong disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? tRoot("loading_ellipsis") : t("confirm_delete_alert")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlertDeleteDialog;
